import { ref } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable para gerenciar favoritos do usuário.
 * Persiste no backend via API e mantém cache em localStorage como fallback.
 */
export function useFavoritos () {
  const authStore = useAuthStore()
  const favoritos = ref([])
  const loading = ref(false)
  const error = ref(null)

  const STORAGE_KEY = `ct-comex-favoritos-${authStore.utilizador?.id || 'anonymous'}`

  /**
   * Carrega favoritos do backend.
   * Fallback para localStorage se a API falhar.
   * Usa _suppressLoading para não acionar o loading global.
   */
  const carregarFavoritos = async () => {
    const userId = authStore.utilizador?.id
    console.log('[useFavoritos] carregarFavoritos chamado. userId do authStore:', userId, '| utilizador:', JSON.stringify(authStore.utilizador))
    if (!userId) {
      favoritos.value = []
      console.log('[useFavoritos] SEM userId, favoritos = []')
      return []
    }

    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/Usuarios/${userId}/favoritos`, { _suppressLoading: true })
      const dados = response.data || []
      favoritos.value = dados
      console.log('[useFavoritos] GET /favoritos retornou', dados.length, 'itens:', JSON.stringify(dados))
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dados))
      return dados
    } catch (e) {
      console.warn('[useFavoritos] Erro ao carregar favoritos da API, usando cache local:', e)
      error.value = 'Não foi possível carregar favoritos.'
      try {
        const cached = localStorage.getItem(STORAGE_KEY)
        const dados = cached ? JSON.parse(cached) : []
        favoritos.value = dados
        console.log('[useFavoritos] Usou cache local:', dados.length, 'itens')
        return dados
      } catch {
        favoritos.value = []
        return []
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Salva favoritos no backend (substituição completa).
   */
  const salvarFavoritos = async (lista) => {
    const userId = authStore.utilizador?.id
    if (!userId) return

    loading.value = true
    error.value = null

    try {
      await api.put(`/Usuarios/${userId}/favoritos`, {
        favoritos: lista.map((item, index) => ({
          idRegraAcesso: item.idRegraAcesso,
          ordem: item.ordem || index + 1
        }))
      })
      favoritos.value = [...lista]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favoritos.value))
    } catch (e) {
      console.error('[useFavoritos] Erro ao salvar favoritos:', e)
      error.value = 'Erro ao salvar favoritos.'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Verifica se um idRegraAcesso está favoritado.
   */
  const isFavorito = (idRegraAcesso) => {
    return favoritos.value.some(f => f.idRegraAcesso === idRegraAcesso)
  }

  /**
   * Filtra a lista de favoritos para conter apenas itens
   * que o usuário ainda tem permissão (RBAC).
   */
  const filtrarPorPermissao = (menuSearchIndex) => {
    const modulesPermitidos = new Set(menuSearchIndex.map(m => m.perm))
    return favoritos.value.filter(f => modulesPermitidos.has(f.chave))
  }

  return {
    favoritos,
    loading,
    error,
    carregarFavoritos,
    salvarFavoritos,
    isFavorito,
    filtrarPorPermissao
  }
}
